import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { DialogAtendimentoEncerrado } from '@/components/dialog-atendimento-encerrado'
import { useCriarAtendimento } from '@/hooks/use-criar-atendimento'
import { useEncerrarAtendimento } from '@/hooks/use-encerrar-atendimento'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { showToast } from '@/infra/sonner'
import type { TAtendimento } from '@/types/atendimento'
import { AtualizadorOtimistaUltimosAtendimentos } from '@/utils/atualizador-otimista-ultimos-atendimentos'

const CLIENTE_INATIVO = 'INACTIVE_CLIENT'

export const useAtendimento = () => {
  const [atendimento, setAtendimento] = useLocalStorage<TAtendimento | null>('atendimento', null)
  const { mutateAsync: criarAtendimento, isPending: isCriandoAtendimento } = useCriarAtendimento()
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'atendimento'])
  const { mutateAsync: encerrarAtendimento } = useEncerrarAtendimento()

  const handleIniciarAtendimento = useCallback(
    async (clienteId: string, onSuccess?: VoidFunction) => {
      await criarAtendimento(clienteId, {
        onSuccess: (res) => {
          setAtendimento({ ...res.data, cliente: { ...res.data.cliente, id: clienteId } })
          onSuccess?.()
        },
        onError: (error) => {
          const erroClienteInativo = error?.response?.data?.find((e) => e.code === CLIENTE_INATIVO)
          if (erroClienteInativo) {
            showToast({
              message: t('atendimento:errors.cliente_inativo'),
              variant: 'error',
            })
            return
          }

          showToast({
            message: t('errors_gerais.erro_interno'),
            variant: 'error',
          })
        },
      })
    },
    [criarAtendimento, setAtendimento, t],
  )

  const handleEncerrarAtendimento = useCallback(async () => {
    if (!atendimento) return
    await encerrarAtendimento(atendimento.id, {
      onSuccess: (response) => {
        const optimistic = new AtualizadorOtimistaUltimosAtendimentos()
        optimistic.atualizar(atendimento)
        DialogAtendimentoEncerrado.show({
          duracao: response?.data.duracao,
          nome: atendimento.cliente.nome,
          imagem: atendimento.cliente?.imagem,
        })

        setAtendimento(null)
        navigate('/')
      },
      onError: (error) => {
        if (error.response?.data.some((err) => err.message.includes('já está encerrado'))) {
          showToast({ message: t('atendimento:errors.ja_encerrado'), variant: 'info' })
          setAtendimento(null)
          return
        }
        showToast({ message: t('errors_gerais.erro_interno'), variant: 'error' })
      },
    })
  }, [atendimento, encerrarAtendimento, navigate, setAtendimento, t])

  const value = useMemo(
    () => ({
      atendimento,
      atualizarAtendimento: setAtendimento,
      handleIniciarAtendimento,
      handleEncerrarAtendimento,
      isIniciandoAtendimento: isCriandoAtendimento,
      isEmAtendimento: Boolean(atendimento?.id),
    }),
    [atendimento, handleEncerrarAtendimento, handleIniciarAtendimento, isCriandoAtendimento, setAtendimento],
  )
  return value
}
